import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { useState } from "react"
import { api } from "../../../../convex/_generated/api"
import type { Id } from "../../../../convex/_generated/dataModel"
import { inNumberRange, parseNumberDraft } from "@/lib/numberDraft"

export const Route = createFileRoute("/_authed/admin/campaigns")({
  component: AdminCampaigns,
})

function AdminCampaigns() {
  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight">Campaigns</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-foreground)]">
          Bonus Wild Points windows. Stack with the paid-tier multiplier
          (campaign + tier multiply together).
        </p>
      </header>
      <NewCampaign />
      <CampaignsList />
    </div>
  )
}

function NewCampaign() {
  const venues = useQuery(api.venues.listAll)
  const create = useMutation(api.campaigns.create)
  const [open, setOpen] = useState(false)
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [multiplierDraft, setMultiplierDraft] = useState("2")
  const [eligibility, setEligibility] = useState<
    "all" | "paid_only" | "venue_home"
  >("all")
  const [venueId, setVenueId] = useState<Id<"venues"> | "">("")
  const [startsLocal, setStartsLocal] = useState(() => isoLocalNow())
  const [endsLocal, setEndsLocal] = useState(() => isoLocalNow(7 * 24))
  const [submitting, setSubmitting] = useState(false)
  const multiplier = parseNumberDraft(multiplierDraft)
  const multiplierValid = inNumberRange(multiplier, { min: 1, max: 100 })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!multiplierValid || multiplier === null) return
    setSubmitting(true)
    try {
      await create({
        name,
        description: description || undefined,
        multiplier,
        eligibility,
        venueId:
          eligibility === "venue_home" && venueId
            ? (venueId as Id<"venues">)
            : undefined,
        startsAt: new Date(startsLocal).getTime(),
        endsAt: new Date(endsLocal).getTime(),
        active: true,
      })
      setName("")
      setDescription("")
      setMultiplierDraft("2")
      setOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-md border border-dashed border-[color:var(--border)] px-3 py-1.5 text-xs font-medium hover:bg-[color:var(--muted)]"
      >
        + New campaign
      </button>
    )
  }
  return (
    <form
      onSubmit={handleSubmit}
      className="rounded-md border border-[color:var(--border)] p-4 space-y-3"
    >
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold">New campaign</h2>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-[color:var(--muted-foreground)]"
        >
          Cancel
        </button>
      </div>
      <input
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        aria-label="Campaign name"
        placeholder="Campaign name"
        required
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      <textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        aria-label="Campaign description"
        placeholder="Description (optional)"
        rows={2}
        className="w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-3 py-2 text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">
            Multiplier
          </span>
          <input
            type="text"
            inputMode="decimal"
            value={multiplierDraft}
            onChange={(e) => setMultiplierDraft(e.target.value)}
            aria-invalid={!multiplierValid || undefined}
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">
            Eligibility
          </span>
          <select
            value={eligibility}
            onChange={(e) =>
              setEligibility(
                e.target.value as "all" | "paid_only" | "venue_home",
              )
            }
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          >
            <option value="all">All players</option>
            <option value="paid_only">Wild Pass only</option>
            <option value="venue_home">Home venue match</option>
          </select>
        </label>
        {eligibility === "venue_home" && (
          <label className="text-xs">
            <span className="text-[color:var(--muted-foreground)]">Venue</span>
            <select
              value={venueId}
              onChange={(e) => setVenueId(e.target.value as Id<"venues">)}
              required
              className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
            >
              <option value="">Select…</option>
              {venues?.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.name}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">Starts</span>
          <input
            type="datetime-local"
            value={startsLocal}
            onChange={(e) => setStartsLocal(e.target.value)}
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          />
        </label>
        <label className="text-xs">
          <span className="text-[color:var(--muted-foreground)]">Ends</span>
          <input
            type="datetime-local"
            value={endsLocal}
            onChange={(e) => setEndsLocal(e.target.value)}
            className="mt-1 w-full rounded-md border border-[color:var(--border)] bg-[color:var(--muted)] px-2 py-1 text-sm"
          />
        </label>
      </div>
      <button
        type="submit"
        disabled={submitting || !name.trim() || !multiplierValid}
        className="rounded-md bg-[color:var(--primary)] px-3 py-1.5 text-xs font-medium text-[color:var(--primary-foreground)] disabled:opacity-50"
      >
        {submitting ? "Creating…" : "Launch campaign"}
      </button>
    </form>
  )
}

function CampaignsList() {
  const campaigns = useQuery(api.campaigns.listAll)
  const update = useMutation(api.campaigns.update)
  const remove = useMutation(api.campaigns.remove)
  if (!campaigns) {
    return <p className="text-sm text-[color:var(--muted-foreground)]">Loading…</p>
  }
  if (campaigns.length === 0) {
    return (
      <p className="text-sm text-[color:var(--muted-foreground)]">
        No campaigns yet.
      </p>
    )
  }
  const now = Date.now()
  return (
    <ul className="divide-y divide-[color:var(--border)] rounded-md border border-[color:var(--border)]">
      {campaigns.map((c) => {
        const isCurrent = c.active && c.startsAt < now && c.endsAt > now
        const isFuture = c.startsAt > now
        const isPast = c.endsAt < now
        return (
          <li key={c._id} className="px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="font-display text-sm font-semibold">{c.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[color:var(--muted-foreground)]">
                  {c.multiplier}× ·{" "}
                  {c.eligibility === "all"
                    ? "all players"
                    : c.eligibility === "paid_only"
                      ? "Wild Pass only"
                      : `home venue: ${c.venue?.name ?? "—"}`}
                </p>
                <p className="mt-1 text-xs text-[color:var(--muted-foreground)]">
                  {new Date(c.startsAt).toLocaleString()} →{" "}
                  {new Date(c.endsAt).toLocaleString()}
                </p>
                {c.description && (
                  <p className="mt-1 text-xs">{c.description}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span
                  className={[
                    "rounded-full px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.15em]",
                    isCurrent
                      ? "bg-[color:var(--accent)]/30"
                      : isFuture
                        ? "bg-[color:var(--muted)]"
                        : "bg-[color:var(--muted)] text-[color:var(--muted-foreground)]",
                  ].join(" ")}
                >
                  {isCurrent ? "active" : isFuture ? "scheduled" : "ended"}
                </span>
                <button
                  onClick={() =>
                    update({ id: c._id, patch: { active: !c.active } })
                  }
                  className="rounded border border-[color:var(--border)] px-2 py-0.5 text-[10px]"
                >
                  {c.active ? "Pause" : "Resume"}
                </button>
                {isPast && (
                  <button
                    onClick={() => {
                      if (confirm("Delete this past campaign?"))
                        remove({ id: c._id })
                    }}
                    className="text-[10px] text-red-600"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          </li>
        )
      })}
    </ul>
  )
}

function isoLocalNow(hoursAhead: number = 0): string {
  const d = new Date(Date.now() + hoursAhead * 60 * 60 * 1000)
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  const h = String(d.getHours()).padStart(2, "0")
  const min = String(d.getMinutes()).padStart(2, "0")
  return `${y}-${m}-${day}T${h}:${min}`
}
